import { useEffect, useRef } from 'react'
import { MobileDrawerNav } from './MobileDrawerNav'

interface MobileDrawerProps {
	isOpen: boolean
	onClose: () => void
}

const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'

export function MobileDrawer({ isOpen, onClose }: MobileDrawerProps) {
	const drawerRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (!isOpen) return
		function handleKey(e: KeyboardEvent) {
			if (e.key === 'Escape') onClose()
		}
		document.addEventListener('keydown', handleKey)
		return () => document.removeEventListener('keydown', handleKey)
	}, [isOpen, onClose])

	useEffect(() => {
		document.body.style.overflow = isOpen ? 'hidden' : ''
		return () => {
			document.body.style.overflow = ''
		}
	}, [isOpen])

	useEffect(() => {
		if (!isOpen) return
		const drawer = drawerRef.current
		if (!drawer) return

		const getFocusable = () => Array.from(drawer.querySelectorAll<HTMLElement>(FOCUSABLE))
		getFocusable()[0]?.focus()

		function handleTab(e: KeyboardEvent) {
			if (e.key !== 'Tab') return
			const els = getFocusable()
			if (els.length === 0) return
			const first = els[0]
			const last = els[els.length - 1]
			if (e.shiftKey) {
				if (document.activeElement === first) {
					e.preventDefault()
					last.focus()
				}
			} else {
				if (document.activeElement === last) {
					e.preventDefault()
					first.focus()
				}
			}
		}

		document.addEventListener('keydown', handleTab)
		return () => document.removeEventListener('keydown', handleTab)
	}, [isOpen])

	return (
		<>
			<div
				className={`fixed inset-0 z-40 bg-black/50 transition-opacity duration-200 md:hidden ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
				aria-hidden="true"
				onClick={onClose}
			/>

			<div
				ref={drawerRef}
				role="dialog"
				aria-modal="true"
				aria-label="Navigation"
				className={`fixed bottom-0 left-0 right-0 z-50 bg-chrome rounded-t-2xl p-4 pb-safe transition-transform duration-300 ease-out md:hidden ${isOpen ? 'translate-y-0' : 'translate-y-full'}`}
				style={{ maxHeight: '85dvh', overflowY: 'auto' }}
			>
				<div className="w-10 h-1 bg-chrome-foreground/20 rounded-full mx-auto mb-5" />
				<img src="/logo_ricepaper.svg" alt="SAYSAY" className="mb-4 ml-1 size-11" />
				<MobileDrawerNav isOpen={isOpen} onNavigate={onClose} />
			</div>
		</>
	)
}
